<script setup lang="ts">
import {
   useStorePrice, useStorePriceLoading,
} from "~/composables/store/order/mainForm";
import {
  useStorePromoCode,
  useStoreSelectedService, useStoreSelectedServiceChildren, useStoreSelectedServiceId
} from "~/composables/store/order/serviceForm";
import {
  useStoreOrderClientForm,
} from "~/composables/store/order/orderForm";
import {useHandleGetServicePrice} from "~/composables/store/services/getServicePrice";
import {useStoreServiceInputs} from "~/composables/store/services/service_inputs";
import FormSelectList from "~/components/component/Service/Forms/FormSelectList.vue";
import {
  INPUT_INTEGER_SELECT,
  INPUT_TYPE_INTEGER,
  INPUT_TYPE_QUANTITY,
  INPUT_TYPE_SELECT
} from "~/constants/values/services/input_types";
import FormInputInteger from "~/components/component/Service/Forms/FormInputInteger.vue";
import FormSelectInteger from "~/components/component/Service/Forms/FormSelectInteger.vue";
import BigQuadratureOrderForm from "~/components/component/Service/order/forms/services/BigQuadratureOrderForm.vue";
import BigQuadratureInfo from "~/components/component/Service/order/forms/services/BigQuadratureInfo.vue";
import QuadraturesForm from "~/components/component/Service/order/forms/services/QuadraturesForm.vue";
import FormSelectFromList from "~/components/component/Service/order/InputsForms/FormSelectFromList.vue";
import FormSetQuantity from "~/components/component/Service/order/InputsForms/FormSetQuantity.vue";
import FormSelectService from "~/components/component/Service/order/InputsForms/FormSelectService.vue";
import FormFrequency from "~/components/component/Service/order/InputsForms/FormCleaningFrequency.vue";
import FormAdditionalServices from "~/components/component/Service/order/InputsForms/FormAdditionalServices.vue";
import debounce from "lodash.debounce";

const t = useI18n().t
const nuxtApp = useNuxtApp()
const route = useRoute()
const isBccPage = computed(() => route.name === 'bcc')
const props = defineProps({
  services: {
    type: Array,
    default: () => []
  },
  isFilled: Boolean,
  order: Number
})
const service = useStoreSelectedService()
const emit = defineEmits(['close-modal', 'next', 'back'])
const {isMobile} = useDevice()

const servicesWithInputs = props.services.filter(serv => Object.keys(serv.input_fields)?.length > 0);
function closeModal() {
  emit('close-modal')
}

function goNext() {
  emit('next')
}
function goBack() {
  emit('back', props.order)
}

let loadingPrice = ref(false)

// async function getServicePrice() {
//   await useHandleGetServicePrice(loadingPrice)
// }

const priceLoading = useStorePriceLoading()
const getServicePrice = debounce(async () => {
  await nuxtApp.runWithContext(() => useHandleGetServicePrice(loadingPrice))
}, 500)

// флаг поднимаем сразу по клику, до debounce: иначе полсекунды показывается сумма
// от прошлого набора комнат, а после смены типа уборки — ноль
function schedulePriceUpdate() {
  priceLoading.value = true
  getServicePrice()
}

// уходим с шага раньше, чем сработал debounce — досчитываем сразу, чтобы дальше
// по оформлению не ехала устаревшая цена
onBeforeUnmount(() => getServicePrice.flush())

const inputStore = useStoreServiceInputs()

function copyWithoutKeys(obj, keysToExclude) {
  return Object.keys(obj).reduce((newObj, key) => {
    if (!keysToExclude.includes(key) && !key.includes('repetition')) {
      newObj[key] = obj[key];
    }
    return newObj;
  }, {});
}

const uncommonInputStore = computed(() => {
  if (!inputStore.value) return {}
  return copyWithoutKeys(inputStore.value, ['home_type', 'room_count', 'bathroom_count'])
})

const inputStoreLength = computed(() => Object.keys(inputStore.value).length)
const servicesChangedWatcher = ref(false)

function handleChangeService(selectedService: any) {
  useStoreSelectedServiceId().value = selectedService.service_id
  service.value = selectedService
  inputStore.value = selectedService.input_fields
}
if (!useStoreSelectedServiceId().value){
  schedulePriceUpdate()
}
watch(useStoreSelectedServiceId(), () => {
  useStoreDurationJob().value = 0
  useStoreSelectedServiceChildren().value = {}
  useStorePrice().value = 0
  priceLoading.value = true
  servicesChangedWatcher.value = true
  // цена сброшена в ноль, ждать ещё полсекунды debounce незачем — считаем сразу
  nextTick(() => {
    schedulePriceUpdate()
    getServicePrice.flush()
  })
})
watch(inputStore, () => {
  schedulePriceUpdate()
},{deep:true})


watch(useStoreSelectedServiceChildren(), () => {
  if (!servicesChangedWatcher.value) {
    schedulePriceUpdate()
  }
}, {deep: true})

const isBigQuadrature = computed(() => inputStore.value.quadrature?.default_value > 999)
const form = useStoreOrderClientForm()
const {$toast, $toastError} = nuxtApp

function findRepetitionField(inputFields) {
  for (const key in inputFields) {
    if (key.includes('repetition')) {
      return inputFields[key];
    }
  }
  return null;
}
</script>

<template>
  <div class="flex gap-4 justify-between">
    <div
        class="order_section px-0 py-[6px]"
        :class="{ 'bg-primary-green bg-opacity-10' : isFilled }"
    >
      <div v-if="isFilled" class="md:px-5 px-3">
        <div class="flex justify-between items-center gap-3">
          <h2 class="h3">1. {{ $t('order_page.titles.cleaning_data') }}</h2>

          <UIBtn
              @click="goBack"
              border="green"
          >{{ $t('input_forms.button.edit') }}</UIBtn>
        </div>

        <p v-if="isFilled" class="mt-3 md:text-base text-sm font-normal text-secondary-800">
          <span>{{ $t(`services.common_cleaning_services.${service?.keyword}`) }}{{inputStoreLength ? ', ' : ''}}</span>
          <template v-for="(selectedValue, key, index) in inputStore">
            <span v-if="selectedValue.option">{{selectedValue.option}}{{inputStoreLength !== index + 1 ? ', ' : ''}}</span>
            <span v-else-if="selectedValue.default_value && !key.toString().includes('repetition')">{{selectedValue.field_name}} {{selectedValue.default_value}}{{inputStoreLength !== index + 1 ? ', ' : ''}}</span>
          </template>
        </p>
      </div>

      <template v-if="!isFilled">
        <div class="flex flex-col gap-2 px-[6px]">
          <div
              v-if="inputStore?.home_type || inputStore?.room_count || inputStore?.bathroom_count"
              class="form_block"
          >
            <FormSelectFromList
                class="px-[6px]"
                v-if="inputStore?.home_type"
                :field="inputStore?.home_type"
            />

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormSetQuantity
                  v-if="inputStore?.room_count"
                  :field="inputStore?.room_count"
                  @changed="servicesChangedWatcher = false"
              />
              <FormSetQuantity
                  v-if="inputStore?.bathroom_count"
                  :field="inputStore?.bathroom_count"
                  @changed="servicesChangedWatcher = false"
              />
            </div>
          </div>

          <template v-if="isBigQuadrature">
            <div class="grid md:grid-cols-2 grid-cols-1 form_block">
              <BigQuadratureInfo/>
              <QuadraturesForm/>
              <BigQuadratureOrderForm/>
            </div>
          </template>
          <div class="p-5 rounded-xl bg-white" v-else-if="Object.keys(uncommonInputStore).length">
            <p class="text-lg font-bold mb-4">🏢 {{ $t('order_page.titles.room_data') }}</p>
            <div class="flex flex-wrap gap-6">
              <template v-for="(input, key) in uncommonInputStore">
                <FormSetQuantity
                    v-if="input.type === INPUT_TYPE_QUANTITY"
                    :field="input"
                    @changed="servicesChangedWatcher = false"
                />
                <div class="flex flex-col gap-3" v-if="input.type ===INPUT_TYPE_SELECT && !inputStore?.home_type">
                  <p class="order-service-form-text">{{ input.field_name }}</p>
                  <div class="flex flex-row flex-wrap gap-2 ">
                    <FormSelectList :field="input"/>
                  </div>
                </div>

                <div class="flex flex-col gap-3" v-if="input.type ===INPUT_TYPE_INTEGER">
                  <div class="flex flex-row flex-wrap gap-2 ">
                    <FormInputInteger :field=input />
                  </div>
                </div>

                <div class="flex flex-col gap-3" v-if="input.type ===INPUT_INTEGER_SELECT">
                  <div class="flex flex-row flex-wrap gap-2 ">
                    <FormSelectInteger :field=input />
                  </div>
                </div>
              </template>
            </div>
          </div>

          <FormSelectService
              v-if="servicesWithInputs.length"
              v-model="service.service_id"
              :servicesWithInputs
              @changeService="handleChangeService"
          />

          <FormFrequency
              v-if="findRepetitionField(inputStore) && service.keyword === 'standartnaya-uborka'"
              :field="findRepetitionField(inputStore)"
          />

          <div v-if="isMobile && !isBccPage" class="form_block">
            <p class="md:text-lg text-sm font-bold">Промокод</p>
            <ComponentServiceOrderPromoCode/>
          </div>

          <FormAdditionalServices
              v-if="service.children?.length"
              :childrenServices="service.children"
          />
          <UIBtn @click="goNext"
                 color="green"
                 class="mt-6 ml-auto"
          >
            {{ $t('input_forms.button.next') }}
          </UIBtn>
        </div>
      </template>
    </div>

  </div>
</template>

<style scoped>

</style>